<template>
  <div>
    <WorkbookPageContent :page="page" />
    <ClientOnly>
      <UContainer class="mt-12 flex gap-4 items-center">
        <UButton color="primary" variant="solid" icon="i-lucide-download"
          @click="downloadOrderCsv"
          :disabled="!latestOrder"
        >
          Download order file
        </UButton>
        <UButton
          color="primary"
          variant="solid"
          icon="i-lucide-file-text"
          @click="downloadOrderPdf"
          :loading="isExportingPdf"
          :disabled="!latestOrder || isExportingPdf"
        >
          Download your order as PDF
        </UButton>
        <NuxtLink to="/orders" class="ml-4 underline text-primary text-base font-semibold">See all orders</NuxtLink>
        <div v-if="!latestOrder" class="mt-4 text-sm">
          No orders found.
        </div>
      </UContainer>
      <UContainer v-if="latestOrder" class="mb-12">
        <h2 class="mt-12 mb-2 text-xl font-bold">Order summary</h2>
        <div class="text-sm  mb-2">
          Order ID: {{ latestOrder.id }}<br>
          Account name: {{ latestOrder.accountName || '—' }}<br>
          Shipping Address: {{ latestOrder.shippingAddress || '—' }}<br>
          PO#: {{ latestOrder.poNumber || '—' }}<br>
          Requested Ship Date: {{ latestOrder.requestedShipDate ? formatDateToDDMMYYYY(latestOrder.requestedShipDate) : '—' }}
        </div>
        <OrderSummaryTable :rows="latestDisplayRows" :currency="currency" />
      </UContainer>
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useOrdersStore } from '@/store/orders'
import { useAuthStore } from '@/store/auth'
import { usePdfExport } from '@/composables/usePdfExport'
import { useCatalogStore } from '@/store/catalog'
import { useSettingsStore } from '@/store/settings'
import { formatNumber } from '@/utils/formatNumber'
import { normalizeSizeCode, sizeSortIndex } from '@/utils/sizes'
import OrderSummaryTable from '@/components/OrderSummaryTable.vue'
import { usePlausible } from '@/composables/usePlausible'

const route = useRoute()

const { data: page } = await useAsyncData(route.path, () => {
  return queryCollection('pages').path(route.path).first()
})

const ordersStore = useOrdersStore()
ordersStore.hydrate()
const auth = useAuthStore()
const userEmail = computed(() => auth.user?.email || '')
const catalog = useCatalogStore()
const settings = useSettingsStore()
settings.hydrate()
const currency = computed(() => settings.currency)
const { trackEvent } = usePlausible()

function wsFieldForCurrency(cur: string): string {
  switch (cur) {
    case 'USD': return 'WSUSD'
    case 'EUR': return 'WSEUR'
    case 'GBP': return 'WSGBP'
    case 'CHF': return 'WSCHF'
    case 'CAD':
    default:
      return 'WSCAD'
  }
}

// Senaste ordern för user
const latestOrder = computed(() => ordersStore.latestOrder(userEmail.value))

const latestDisplayRows = computed(() => {
  if (!latestOrder.value) return []
  return latestOrder.value.lines.map(line => {
    const variant = catalog.items.find(v => v.SKU === line.sku)
    const model = variant ? catalog.modelByStyle(variant.Style) : null
    const priceField = wsFieldForCurrency(currency.value)
    const price = Number(variant?.[priceField] ?? model?.[priceField] ?? 0)
    return {
      modelName: model?.name ?? '',
      style: variant?.Style ?? '',
      colour: variant?.Colour ?? '',
      size: normalizeSizeCode(variant?.SizeCode ?? variant?.Size ?? ''),
      sku: line.sku,
      qty: line.qty,
      totalPrice: price * line.qty,
      internalId: variant?.InternalIDExportUse ?? ''
    }
  })
})

const sortedDisplayRows = computed(() => {
  return [...latestDisplayRows.value].sort((a, b) => {
    if (a.modelName !== b.modelName) return String(a.modelName).localeCompare(String(b.modelName))
    if (a.style !== b.style) return String(a.style).localeCompare(String(b.style))
    if (a.colour !== b.colour) return String(a.colour).localeCompare(String(b.colour))
    return sizeSortIndex(a.size) - sizeSortIndex(b.size)
  })
})

const latestOrderTotal = computed(() => sortedDisplayRows.value.reduce((sum, row) => sum + row.totalPrice, 0))

function orderCsvFilename(order) {
  const d = new Date(order.timestamp)
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `order-${yyyy}-${mm}-${dd}.csv`
}

function downloadOrderCsv() {
  if (!latestOrder.value) return

  // Track CSV export event
  trackEvent('Export CSV', { 
    orderId: latestOrder.value.id,
    itemCount: latestOrder.value.lines.length
  })

  // Samma format som i orders.vue – export med InternalIDExportUse etc.
  const header = [
    'Account Name',
    'Shipping Address',
    'Requested Ship Date',
    'Sales Order #',
    'PO#',
    'Line ID',
    'Internal ID',
    'Price Level',
    'Quantity',
    'Allocation Strategy'
  ]
  const lines = [header.join(',')]

  for (const line of latestOrder.value.lines) {
    const variant = catalog.items.find(v => v.SKU === line.sku)
    const shipDate = latestOrder.value.requestedShipDate ? formatDateToDDMMYYYY(latestOrder.value.requestedShipDate) : ''
    lines.push([
      escapeCSV(latestOrder.value.accountName || ''),
      escapeCSV(latestOrder.value.shippingAddress || ''),
      shipDate,
      '', // Sales Order #
      escapeCSV(latestOrder.value.poNumber || ''),
      '', // Line ID
      variant?.InternalIDExportUse ?? '', // Internal ID
      '', // Price Level
      line.qty, // Quantity
      '' // Allocation Strategy
    ].join(','))
  }

  const csvContent = lines.join('\r\n')
  const blob = new Blob([csvContent], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = orderCsvFilename(latestOrder.value)
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

const { isExporting, exportWorkbook } = usePdfExport()
const isExportingPdf = computed(() => isExporting.value)

function downloadOrderPdf() {
  if (!latestOrder.value?.styles?.length || isExportingPdf.value) return

  // Track PDF export event
  trackEvent('Export PDF', { 
    orderId: latestOrder.value.id,
    itemCount: latestOrder.value.lines.length
  })

  const cartPayload = {
    saved: latestOrder.value.styles,
    items: Object.fromEntries(latestOrder.value.lines.map(line => [
      line.sku,
      { qty: line.qty }
    ]))
  }
  exportWorkbook({ styles: latestOrder.value.styles, cart: true, cartdata: cartPayload })
}

// Helper function to escape CSV values
function escapeCSV(value: string): string {
  if (!value) return ''
  // If value contains comma, newline, or quote, wrap in quotes and escape quotes
  if (value.includes(',') || value.includes('\n') || value.includes('"')) {
    return '"' + value.replace(/"/g, '""') + '"'
  }
  return value
}

// Helper function to format date from YYYY-MM-DD to DD/MM/YYYY
function formatDateToDDMMYYYY(dateStr: string): string {
  if (!dateStr) return ''
  const [year, month, day] = dateStr.split('-')
  return `${day}/${month}/${year}`
}

useContentSeo(page)
</script>

<style scoped>
table {
  table-layout: fixed;
}
th, td {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}


</style>
